import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { Tabs } from "@package/subframe/components/Tabs";
import {
  FeatherAlertCircle,
  FeatherCheckCircle,
  FeatherHistory,
  FeatherList,
  FeatherPlus,
} from "@subframe/core";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { ComplianceTaskList } from "@/components/compliance/ComplianceTaskList";
import { ComplianceManagerTable } from "@/components/compliance-manager/compliance-manager-table";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useComplianceManagerData } from "@/hooks/use-compliance-manager-data";
import {
  type ComplianceManagerTab,
  type ComplianceSortColumn,
  DEFAULT_SORT,
  PAGE_SIZE,
  SORTABLE_COLUMNS,
  type SortDir,
} from "@/lib/compliance-manager";

const routeApi = getRouteApi("/_authenticated/compliance-manager/");

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-neutral-100 px-2 text-caption-bold font-caption-bold text-neutral-700">
      {count}
    </span>
  );
}

export function ComplianceManagerPage() {
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const { selectedNurseryId, selectedNursery } = useNursery();
  const { userRoleType, isPlatformAdmin } = useAuth();

  const canManage = isPlatformAdmin || userRoleType === "ORG_ADMIN";

  const tab = (search.tab as ComplianceManagerTab) ?? "open";
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<
    ComplianceSortColumn | undefined
  >(undefined);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];

  // Resolve effective sort: explicit column, else the tab's default. A tab can
  // only sort by a column it renders — switching tabs clears the column, but a
  // hand-edited ?tab= would otherwise keep the previous tab's selection.
  const effectiveSort =
    tab === "tasks"
      ? undefined
      : sortColumn && SORTABLE_COLUMNS[tab].includes(sortColumn)
        ? { column: sortColumn, dir: sortDir }
        : DEFAULT_SORT[tab];

  const { items, total, isLoading, counts } = useComplianceManagerData({
    nurseryId: selectedNurseryId ?? undefined,
    tab,
    categoryId,
    sortBy: effectiveSort?.column,
    sortDir: effectiveSort?.dir ?? "asc",
    page,
    pageSize,
  });

  const setTab = (next: ComplianceManagerTab) => {
    setPage(0);
    setSortColumn(undefined);
    navigate({ to: "/compliance-manager", search: { tab: next } });
  };

  const handleSort = (column: ComplianceSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Compliance Manager
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage and track compliance tasks and requirements.
          </span>
        </div>
        {canManage && (
          <Button
            icon={<FeatherPlus />}
            onClick={() => navigate({ to: "/compliance/new" })}
          >
            Add task
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs>
        <Tabs.Item
          active={tab === "open"}
          icon={<FeatherAlertCircle />}
          onClick={() => setTab("open")}
        >
          Open
          <CountBadge count={counts.open} />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "recently-completed"}
          icon={<FeatherCheckCircle />}
          onClick={() => setTab("recently-completed")}
        >
          Recently completed
          <CountBadge count={counts["recently-completed"]} />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "history"}
          icon={<FeatherHistory />}
          onClick={() => setTab("history")}
        >
          History
        </Tabs.Item>
        <Tabs.Item
          active={tab === "tasks"}
          icon={<FeatherList />}
          onClick={() => setTab("tasks")}
        >
          Tasks
        </Tabs.Item>
      </Tabs>

      {tab === "tasks" ? (
        <ComplianceTaskList embedded />
      ) : (
        <div className="flex w-full flex-col items-start gap-4 pb-8">
          {/* Filters: category only — tabs handle the rest */}
          <div className="flex w-full flex-wrap items-center gap-3">
            <Select
              placeholder="All categories"
              value={categoryId ?? "ALL"}
              onValueChange={(val) => {
                setCategoryId(val === "ALL" ? undefined : val);
                setPage(0);
              }}
            >
              <Select.Item value="ALL">All categories</Select.Item>
              {categories.map((category) => (
                <Select.Item key={category.id} value={category.id}>
                  {category.name}
                </Select.Item>
              ))}
            </Select>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex w-full items-center justify-center py-12">
              <Spinner className="h-8 w-8 text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex w-full items-center justify-center py-16">
              <span className="text-body font-body text-subtext-color">
                No compliance tasks to show for this view.
              </span>
            </div>
          ) : (
            <>
              <TableScrollContainer>
                <ComplianceManagerTable
                  items={items}
                  categories={categories}
                  nurseryName={selectedNursery?.name ?? "—"}
                  tab={tab}
                  activeColumn={effectiveSort?.column}
                  activeDir={effectiveSort?.dir ?? "asc"}
                  onSort={handleSort}
                  onView={(id) =>
                    navigate({ to: `/compliance/instance/${id}` })
                  }
                />
              </TableScrollContainer>

              <Pagination
                page={page + 1}
                pageSize={pageSize}
                totalCount={total}
                onPageChange={(next) => setPage(next - 1)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                itemNoun="tasks"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ComplianceManagerPage;
